/**
 * Row-Model fuer das Multi-Range-Modal.
 *
 * Jede Zeile ist eine eigenstaendige Date-Range mit ihren ARI-Werten. "unverändert" wird
 * vom UI als leeres Input dargestellt; Mapping zum Backend-Null in `toBackendRange`.
 *
 * Availability ist Tri-State:
 *   '' (unveraendert) | 'available' | 'blocked'
 */
import type { AriPushBatchRange } from '@/shared/api';
import { maxStayNumberOrNull, stayNumberOrNull } from './helpers';

export type AvailabilityMode = '' | 'available' | 'blocked';

/** Eine Beschränkung über den Zeitraum: '' gleich lassen, 'set' setzen, 'clear' aufheben. */
export type RestrictionMode = '' | 'set' | 'clear';

/** Gleich bleibt null, damit eine Sendung nichts anfasst, was niemand ausgewählt hat. */
export function restrictionValue(mode: RestrictionMode): boolean | null {
  return mode === 'set' ? true : mode === 'clear' ? false : null;
}

export interface MultiRangeRow {
  id: string;
  from: string;
  to: string;
  rate: string;
  availability: AvailabilityMode;
  minStay: string;
  maxStay: string;
  closedToArrival: RestrictionMode;
  closedToDeparture: RestrictionMode;
  /**
   * Channex Stop-Sell-Flag — SEPARAT zu availability. Erlaubt Vermieter, einen Tag
   * fuer Neu-Buchungen zu sperren ohne den Preis oder die Restzeit-Verfuegbarkeit
   * anzutasten. UI exponiert das in der erweiterten Restriktions-Spalte.
   */
  stopSell: RestrictionMode;
  status: 'idle' | 'pushed' | 'error';
}

let rowCounter = 0;

export function newMultiRangeRow(defaultDate?: string): MultiRangeRow {
  rowCounter++;
  const today = defaultDate ?? new Date().toISOString().slice(0, 10);
  return {
    id: `row-${rowCounter}`,
    from: today,
    to: today,
    rate: '',
    availability: '',
    minStay: '',
    maxStay: '',
    closedToArrival: '',
    closedToDeparture: '',
    stopSell: '',
    status: 'idle',
  };
}

export function isMultiRangeRowValid(row: MultiRangeRow): boolean {
  if (!row.from || !row.to) return false;
  if (row.to < row.from) return false;
  return rowHasAnyValue(row);
}

export function rowHasAnyValue(row: MultiRangeRow): boolean {
  if (row.rate.trim()) return true;
  if (row.availability) return true;
  if (row.minStay.trim()) return true;
  if (row.maxStay.trim()) return true;
  if (row.closedToArrival) return true;
  if (row.closedToDeparture) return true;
  if (row.stopSell) return true;
  return false;
}

export function toBackendRange(row: MultiRangeRow): AriPushBatchRange {
  // Stop-Sell ist eigenstaendig zur Availability — es wird nur gesendet, wenn jemand es setzt
  // oder aufhebt. Kein Auto-Trigger aus availability='blocked', weil ein reiner
  // Availability-Push dann /restrictions unnoetig aufmachen wuerde (zwei API-Calls statt einem).
  // Fuer den Kombo-Fall (Tag komplett blocken UND stop_sell): beide Kontrollen setzen.
  return {
    from: row.from,
    to: row.to,
    rate: row.rate.trim() ? Number(row.rate.replace(',', '.')) : null,
    available:
      row.availability === 'available' ? true : row.availability === 'blocked' ? false : null,
    // MaxStay=0 ist der ausdrueckliche Wert "unbegrenzt"; leere Felder bleiben null.
    minStay: stayNumberOrNull(row.minStay),
    maxStay: maxStayNumberOrNull(row.maxStay),
    closedToArrival: restrictionValue(row.closedToArrival),
    closedToDeparture: restrictionValue(row.closedToDeparture),
    stopSell: restrictionValue(row.stopSell),
  };
}
